// The business's day on a station (B-050, B-051): the account's IANA zone and trading-day
// cut-off, synced down with the account row (stream `account`). "Today" is the trading day:
// before the cut-off it is still the day before (a bakery whose day runs to 04:00 bakes the
// night's batches for the day before), as on the server, where a batch's planned, completed
// and cancelled days, a movement's trading day and a state task's due day are all this day.
// Until the first sync brings the row, SAST with a 00:00 cut-off (Crumble's).

import { businessToday, DEFAULT_TIME_ZONE, isValidTimeZone } from '@enp/domain';

/** The business's zone and trading-day cut-off. */
export interface BusinessDaySettings {
  /** IANA zone, e.g. `Africa/Johannesburg`. */
  timeZone: string;
  /** Trading-day cut-off, `HH:mm` (`00:00` = the calendar day). */
  cutoff: string;
}

/** The business's day as the station keeps it: its settings and the day it is now. */
export interface BusinessDayState extends BusinessDaySettings {
  /** Today's trading day, `YYYY-MM-DD`. */
  today: string;
}

/** What a station goes by until the account row syncs: SAST, a midnight cut-off. */
export const DEFAULT_BUSINESS_DAY: BusinessDaySettings = Object.freeze({
  timeZone: DEFAULT_TIME_ZONE,
  cutoff: '00:00',
});

/** The synced account row (stream `account`): one row, the station's own account. */
export const BUSINESS_DAY_SQL = 'SELECT timezone, trading_day_cutoff FROM account LIMIT 1';

export interface BusinessDayRow {
  timezone: string | null;
  trading_day_cutoff: string | null;
}

// A Postgres `time` arrives as text: `04:00:00` (cut-offs are whole minutes).
const CUTOFF_RE = /^([01]\d|2[0-3]):([0-5]\d)(?::00(?:\.0+)?)?$/;

/**
 * The settings in a synced account row. A value that is missing or not usable (an unknown
 * zone, a malformed cut-off) falls back to the default, each on its own.
 */
export function businessDaySettings(row: BusinessDayRow | undefined): BusinessDaySettings {
  const zone = row?.timezone;
  const cutoff = CUTOFF_RE.exec(row?.trading_day_cutoff ?? '');
  return {
    timeZone: zone && isValidTimeZone(zone) ? zone : DEFAULT_BUSINESS_DAY.timeZone,
    cutoff: cutoff ? `${cutoff[1]}:${cutoff[2]}` : DEFAULT_BUSINESS_DAY.cutoff,
  };
}

/** The business's trading day at `now`. */
export function businessDayAt(settings: BusinessDaySettings, now: Date): string {
  return businessToday(settings.timeZone, { now, cutoff: settings.cutoff });
}
